import { ColumnDef, FilterFnOption } from '@tanstack/react-table';
import { MarketplaceSquareLogo } from 'src/components/common/MarketplaceSquareLogo';
import { getSeatDisplayText } from 'src/components/common/SeatingInfo';
import { ListingNotesDisplay } from 'src/components/Listings/ListingDisplay/ListingNotesDisplay';
import { BaseCell, SvgCell } from 'src/components/TableV2/cells';
import { ColumnPicker } from 'src/components/TableV2/components/ColumnPicker';
import { useAppContext } from 'src/contexts/AppContext';
import {
  COLUMN_DEFAULT_SIZE_BASE,
  COLUMN_DEFAULT_SIZE_BASE_L,
  COLUMN_DEFAULT_SIZE_L,
  COLUMN_DEFAULT_SIZE_LG,
  COLUMN_DEFAULT_SIZE_XL,
  COLUMN_MAX_SIZE,
  COLUMN_MIN_SIZE,
} from 'src/contexts/ColumnResizingContext/ColumnResizingContext.types';
import { Content } from 'src/contexts/ContentContext';
import { WarningMessage } from 'src/core/POS/MessageWithIcon';
import { vars } from 'src/core/themes';
import { Stack, TooltipPopover } from 'src/core/ui';
import { TableHeaderCellDiv } from 'src/core/ui/TableHeaderCellDiv';
import { useConsigneeAccountDisplayName } from 'src/hooks/api/useConsigneeAccountDisplayName';
import { useColumnUserSetting } from 'src/hooks/useColumnUserSetting';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { FormatOption } from 'src/modals/EditTableColumns';
import { LaunchUploadBarcodes } from 'src/modals/UploadBarcodes/LaunchUploadBarcodes';
import { IconsFill, WarningOutlineIcon } from 'src/svgs/Viagogo';
import { ListingWithEvent } from 'src/tables/ListingTable/ListingTable.types';
import { isMarketplace } from 'src/tables/PurchaseTable/configs/PurchaseTableColumnsConfigV2';
import { TableShimmeringDiv } from 'src/tables/Table';
import { InHandCell } from 'src/tables/Table/TableCells';
import { createCopyColumn } from 'src/tables/TableV2/factories/copyColumnFactory';
import { createDateColumn } from 'src/tables/TableV2/factories/dateColumnFactory';
import { createIconTagColumn } from 'src/tables/TableV2/factories/iconTagColumnFactory';
import { createLinkColumn } from 'src/tables/TableV2/factories/linkColumnFactory';
import { createMoneyColumn } from 'src/tables/TableV2/factories/moneyColumnFactory';
import { createNumberColumn } from 'src/tables/TableV2/factories/numberColumnFactory';
import { createPercentColumn } from 'src/tables/TableV2/factories/percentColumnFactory';
import { createSeatingColumn } from 'src/tables/TableV2/factories/seatingColumnFactory';
import {
  createStatusColumn,
  MARKETPLACE_SALE_PAYMENT_STATUS_MAPPING,
  SALE_PAYMENT_STATUS_MAPPING,
  SALE_STATUS_MAPPING,
} from 'src/tables/TableV2/factories/statusColumnFactory';
import { createTextColumn } from 'src/tables/TableV2/factories/textColumnFactory';
import { createUserTagColumn } from 'src/tables/TableV2/factories/userTagColumnFactory';
import {
  useApiFetchedUser,
  useContextBasedUsers,
} from 'src/tables/TableV2/factories/userTagColumnHooks';
import {
  booleanFilter,
  dateRangeFilter,
  multiSelectFilter,
  seatRangeFilter,
  seatTraitsFilter,
  textSearchFilter,
} from 'src/tables/TableV2/filters/filterFunctions';
import { ContentId } from 'src/utils/constants/contentId';
import {
  MARKETPLACE_SALE_PAYMENT_STATE_TO_CID,
  SALE_PAYMENT_STATE_TO_CID,
  SALE_TABLE_V2_COLUMN_ID_TO_CONTENT_ID,
  SALES_STATUS_TO_CID,
  TICKET_TYPE_TO_CID,
} from 'src/utils/constants/contentIdMaps';
import { FormatContentId } from 'src/utils/constants/formatContentId';
import { isInFullPageEventView } from 'src/utils/deepLinkUtils';
import { getPurchaseOrderRelativeUrl } from 'src/utils/purchaseUtils';
import {
  getSaleDetailsRelativeUrl,
  getSaleStatusDisplayContentId,
  getSaleStatusIcon,
} from 'src/utils/saleUtils';
import {
  safeSortDateTime,
  sortGeneric,
  sortSeatingSeatsForTable,
} from 'src/utils/tableUtils';
import { getTicketTypeIcon } from 'src/utils/ticketTypeUtils';
import { SectionType } from 'src/utils/types/sectionType';
import {
  Feature,
  ListingNote,
  Marketplace,
  MarketplaceFulfillmentState,
  PointOfSalePaymentState,
  PointOfSaleSaleStatus,
  PosClientConfig,
  SaleActionType,
  SaleClient,
  TicketBarcode,
  TicketType,
} from 'src/WebApiController';
import { text } from 'stream/consumers';

import { SalesTableColumnId } from '../../../utils/columns/sales/salesColumnUtils.types';
import {
  SaleWithEvent,
  SHIMMERING_DIV_HEIGHT_SINGLE_SALE,
} from '../SalesTable.type';
import { MarketplaceInfoCell } from './MarketplaceInfoCell';
import { SalesActionsCell } from './SalesActionsCell';

const COST_COLUMN_MIN_SIZE = 75;
const COST_COLUMN_MAX_SIZE = 125;

const SECTION_TYPE = SectionType.Sales;

export const SALE_TABLE_COLUMNS_CONFIG_V2: Partial<
  Record<SalesTableColumnId, ColumnDef<SaleWithEvent | null>>
> = {
  [SalesTableColumnId.Seating]: createSeatingColumn<SaleWithEvent | null>({
    id: SalesTableColumnId.Seating,
    headerContentId:
      SALE_TABLE_V2_COLUMN_ID_TO_CONTENT_ID[SalesTableColumnId.Seating],
    accessor: (data) =>
      data
        ? {
            seating: data.sale.seating,
            listingNotes: data.sale.seatTraits,
            isSeatSaver: data.sale.isSeatSaver,
            status: data.sale.status,
          }
        : null,
    hasMarketplaceProblemAccessor: (data) =>
      data?.sale?.hasMarketplaceProblem ?? false,
    size: COLUMN_DEFAULT_SIZE_XL,
    enableColumnFilter: true,
  }),
  [SalesTableColumnId.SeatTraits]: {
    id: SalesTableColumnId.SeatTraits,
    header: ({ column: { id } }) => (
      <Content
        id={SALE_TABLE_V2_COLUMN_ID_TO_CONTENT_ID[id as SalesTableColumnId]}
      />
    ),
    enableColumnFilter: true,
    filterFn: seatTraitsFilter as FilterFnOption<SaleWithEvent | null>,
    meta: {
      columnFilterVariant: 'multiSelect',
      uniqueValueVariant: 'seatTraits',
    },
    size: COLUMN_DEFAULT_SIZE_LG,
    minSize: COLUMN_MIN_SIZE,
    maxSize: COLUMN_MAX_SIZE,
    accessorFn: (data) => data?.sale?.seatTraits,
    cell: function Cell({ getValue, row: { original } }) {
      const seatTraits = getValue<ListingNote[] | null | undefined>();

      if (original == null) {
        return (
          <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
        );
      }

      return (
        <BaseCell>
          <ListingNotesDisplay seatTraits={seatTraits} />
        </BaseCell>
      );
    },
  },
  [SalesTableColumnId.QuantitySold]: createNumberColumn<SaleWithEvent | null>({
    id: SalesTableColumnId.QuantitySold,
    headerContentId:
      SALE_TABLE_V2_COLUMN_ID_TO_CONTENT_ID[SalesTableColumnId.QuantitySold],
    accessor: (data) => (data ? data.sale.qtySold || 0 : null),
    size: COLUMN_DEFAULT_SIZE_BASE,
    withFooter: true,
    enableColumnFilter: true,
  }),
  [SalesTableColumnId.OrderDate]: createDateColumn<SaleWithEvent | null>({
    id: SalesTableColumnId.OrderDate,
    headerContentId:
      SALE_TABLE_V2_COLUMN_ID_TO_CONTENT_ID[SalesTableColumnId.OrderDate],
    accessor: (data) => data?.sale?.saleDate,
    size: COLUMN_DEFAULT_SIZE_L,
    defaultPrecision: FormatOption.DateTime_MonthDD_Time,
  }),
  [SalesTableColumnId.InHandDate]: {
    id: SalesTableColumnId.InHandDate,
    header: () => <TableHeaderCellDiv align="left">IH</TableHeaderCellDiv>,
    enableColumnFilter: true,
    size: COLUMN_DEFAULT_SIZE_BASE_L,
    minSize: COLUMN_MIN_SIZE,
    maxSize: COLUMN_MAX_SIZE,
    accessorFn: (data) => data?.sale.inHandAt,
    cell: ({ getValue, row: { original } }) => {
      const value = getValue<string | null>();
      if (!original?.sale) {
        return (
          <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
        );
      }

      if (!value) {
        return <BaseCell />;
      }
      return (
        <BaseCell>
          <InHandCell
            date={value ? new Date(value) : null}
            needsToFulfill={Boolean(
              original?.sale.status &&
              ![
                PointOfSaleSaleStatus.ProcessingFulfillment,
                PointOfSaleSaleStatus.Fulfilled,
                PointOfSaleSaleStatus.CancelledHold,
                PointOfSaleSaleStatus.Rejected,
                PointOfSaleSaleStatus.PendingRejection,
              ].includes(original?.sale.status)
            )}
            handleFulfillmentState
          />
        </BaseCell>
      );
    },
    sortingFn: safeSortDateTime<SaleWithEvent>,
    filterFn: dateRangeFilter,
    meta: {
      columnFilterVariant: 'dateRange',
    },
  },
  [SalesTableColumnId.Status]: createStatusColumn({
    id: SalesTableColumnId.Status,
    headerContentId:
      SALE_TABLE_V2_COLUMN_ID_TO_CONTENT_ID[SalesTableColumnId.Status],
    accessor: (data) => data?.sale?.status,
    statusMapping: SALE_STATUS_MAPPING,
    contentMapping: SALES_STATUS_TO_CID,
    size: COLUMN_DEFAULT_SIZE_LG,
    useUserSetting: useColumnUserSetting,
    customCellRenderer: ({
      status,
      statusColors,
      contentMap,
      original,
      userDefinedPrecision,
    }) => {
      const sale = original?.sale;

      const value = contentMap ? contentMap[status as string] : undefined;
      if (!sale) {
        return (
          <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
        );
      }

      const SaleStatusIcon = getSaleStatusIcon(
        status as PointOfSaleSaleStatus,
        sale?.mkpContrState,
        sale?.mkp
      );

      const showVersionMismatchWarning =
        status === PointOfSaleSaleStatus.Fulfilled && sale?.isFulfillVerMis;

      const showMarketplaceFulfilledWarning =
        status !== PointOfSaleSaleStatus.Fulfilled &&
        sale?.mkpFulfState === MarketplaceFulfillmentState.Fulfilled;

      return (
        <BaseCell isFullWidth>
          <Stack direction="row" gap="m" alignItems="center" width="full">
            <div
              style={{
                borderRadius: vars.borderRadius.m,
                padding: `${vars.spacing['xxs']} ${vars.spacing['sm']}`,
                width: '100%',
                ...statusColors,
              }}
            >
              {userDefinedPrecision === FormatOption.EnumDisplay_Icon ? (
                <SaleStatusIcon fill={'transparent'} title={value} />
              ) : (
                value && <Content id={value} />
              )}
            </div>

            {showVersionMismatchWarning && (
              <Stack>
                <TooltipPopover
                  variant="link"
                  triggerContent={
                    <WarningOutlineIcon
                      fill={IconsFill.textWarning}
                      withHoverEffect
                    />
                  }
                >
                  <WarningMessage
                    message={
                      <Content id={ContentId.VersionMismatchExplanation} />
                    }
                  />
                </TooltipPopover>
              </Stack>
            )}

            {showMarketplaceFulfilledWarning && (
              <Stack>
                <TooltipPopover
                  variant="link"
                  triggerContent={
                    <WarningOutlineIcon
                      fill={IconsFill.textWarning}
                      withHoverEffect
                    />
                  }
                >
                  <WarningMessage
                    message={
                      <Content id={ContentId.SaleIsFulfilledOnMarketplace} />
                    }
                  />
                </TooltipPopover>
              </Stack>
            )}
          </Stack>
        </BaseCell>
      );
    },
  }),
  [SalesTableColumnId.TicketType]: {
    id: SalesTableColumnId.TicketType,
    header: ({ column: { id } }) => (
      <TableHeaderCellDiv align="left">
        <Content
          id={SALE_TABLE_V2_COLUMN_ID_TO_CONTENT_ID[id as SalesTableColumnId]}
        />
      </TableHeaderCellDiv>
    ),
    enableColumnFilter: true,
    size: COLUMN_DEFAULT_SIZE_LG,
    minSize: COLUMN_MIN_SIZE,
    maxSize: COLUMN_MAX_SIZE,
    accessorFn: (data) => (data ? data.sale.ticketType : null),
    cell: function Cell({
      getValue,
      column: { id: columnId },
      table,
      row: { original },
    }) {
      const hasSaleEditBarcodeFeature = useUserHasFeature(
        Feature.SaleEditBarcode
      );
      const { userDefinedPrecision } = useColumnUserSetting(
        columnId,
        table.options.meta?.sectionType ?? SECTION_TYPE
      );
      const value = getValue() as TicketType;
      if (value == null) {
        return (
          <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
        );
      }

      if (
        hasSaleEditBarcodeFeature &&
        value === TicketType.Barcode &&
        original?.sale
      ) {
        const isEditable = original?.sale.actions.some(
          (action) =>
            action === SaleActionType.Fulfill ||
            action === SaleActionType.Refulfill
        );

        return (
          <BaseCell>
            <LaunchUploadBarcodes
              isReadOnly={!isEditable}
              onUploadBarcodes={(
                clientConfig: PosClientConfig,
                saleId: number,
                barcodes: TicketBarcode[]
              ) =>
                new SaleClient(clientConfig).updateBarcodesForSale(
                  saleId,
                  barcodes
                )
              }
              entityId={original?.sale.id}
              idOnMkp={original?.sale.idOnMkp}
              barcodeIconOnlyMode={true}
              iconOnlyMode={true}
              iconStyle={{ padding: 0 }}
              cancelTo={undefined}
              variant={undefined}
              action={SaleActionType.Refulfill}
              loadingContentId={FormatContentId.LoadingSaleId}
              errorContentId={FormatContentId.CouldNotFindSaleId}
              searchingContentId={FormatContentId.SearchingForSaleId}
              isSaleNoFulfill={original?.sale.isNoFulfill}
            />
          </BaseCell>
        );
      }
      const ticketTypeIcon = getTicketTypeIcon(value, IconsFill.currentColor);
      return (
        <BaseCell align="left" title={value}>
          {userDefinedPrecision === FormatOption.EnumDisplay_Icon ? (
            <SvgCell
              value={ticketTypeIcon}
              svgWrapperStyle={{
                padding: '4px',
                backgroundColor: vars.color.surfaceSecondary,
                borderRadius: vars.borderRadius.s,
              }}
            />
          ) : (
            <Content id={TICKET_TYPE_TO_CID[value]} />
          )}
        </BaseCell>
      );
    },
    filterFn: multiSelectFilter,
    meta: {
      columnFilterVariant: 'multiSelect',
    },
  },
  [SalesTableColumnId.Proceeds]: createMoneyColumn({
    id: SalesTableColumnId.Proceeds,
    headerContentId:
      SALE_TABLE_V2_COLUMN_ID_TO_CONTENT_ID[SalesTableColumnId.Proceeds],
    accessor: (data) => data?.sale?.ttlNetProcs,
    withFooter: true,
  }),
  [SalesTableColumnId.SoldTicketCost]: createMoneyColumn({
    id: SalesTableColumnId.SoldTicketCost,
    headerContentId:
      SALE_TABLE_V2_COLUMN_ID_TO_CONTENT_ID[SalesTableColumnId.SoldTicketCost],
    accessor: (data) => data?.sale?.soldTktCost,
    withThresholds: true,
    withFooter: true,
  }),
  [SalesTableColumnId.PandL]: createMoneyColumn({
    id: SalesTableColumnId.PandL,
    headerContentId:
      SALE_TABLE_V2_COLUMN_ID_TO_CONTENT_ID[SalesTableColumnId.PandL],
    accessor: (data) => data?.sale?.pandL,
    withThresholds: true,
    withFooter: true,
  }),
  [SalesTableColumnId.Margin]: createPercentColumn<SaleWithEvent | null>({
    id: SalesTableColumnId.Margin,
    headerContentId:
      SALE_TABLE_V2_COLUMN_ID_TO_CONTENT_ID[SalesTableColumnId.Margin],
    accessor: (data) => data?.sale?.margin,
    withThresholds: true,
    withFooter: true,
  }),
  [SalesTableColumnId.Charges]: createMoneyColumn({
    id: SalesTableColumnId.Charges,
    headerContentId:
      SALE_TABLE_V2_COLUMN_ID_TO_CONTENT_ID[SalesTableColumnId.Charges],
    accessor: (data) => data?.sale?.charges,
    withFooter: true,
  }),
  [SalesTableColumnId.Credits]: createMoneyColumn({
    id: SalesTableColumnId.Credits,
    headerContentId:
      SALE_TABLE_V2_COLUMN_ID_TO_CONTENT_ID[SalesTableColumnId.Credits],
    accessor: (data) => data?.sale?.credits,
    withFooter: true,
  }),
  [SalesTableColumnId.Sale]: createLinkColumn<SaleWithEvent | null>({
    id: SalesTableColumnId.Sale,
    headerContentId:
      SALE_TABLE_V2_COLUMN_ID_TO_CONTENT_ID[SalesTableColumnId.Sale],
    linkAccessor: (data) =>
      data?.sale?.id ? getSaleDetailsRelativeUrl(data.sale.id) : null,
    textAccessor: (data) =>
      data?.sale?.id ? String(data.sale.id) : data?.sale?.idOnMkp,
    size: COLUMN_DEFAULT_SIZE_BASE_L,
    sectionType: SectionType.RecentSalesTable,
  }),
  [SalesTableColumnId.Marketplace]: createIconTagColumn<SaleWithEvent | null>({
    id: SalesTableColumnId.Marketplace,
    headerContentId:
      SALE_TABLE_V2_COLUMN_ID_TO_CONTENT_ID[SalesTableColumnId.Marketplace],
    accessor: (data) => data?.sale?.mkp,
    iconMapper: (marketplace) => {
      if (!marketplace || !isMarketplace(marketplace)) {
        return null;
      }
      return (
        <MarketplaceSquareLogo
          marketplace={marketplace as Marketplace}
          size={16}
        />
      );
    },
    showText: true,
    size: COLUMN_DEFAULT_SIZE_BASE,
    enableColumnFilter: true,
  }),
  [SalesTableColumnId.Actions]: {
    id: SalesTableColumnId.Actions,
    enableSorting: false,
    enableResizing: true,
    header: function Cell() {
      const { mainRoute } = useAppContext();
      const isEventPage = isInFullPageEventView(mainRoute);
      return isEventPage ? (
        <ColumnPicker tableColumnsSectionType={SectionType.Sales} />
      ) : null;
    },
    enableColumnFilter: false,
    cell: ({ row: { original }, table }) => {
      const linkOnly =
        table.options.meta?.formatOptionOverrides?.[
          SalesTableColumnId.Actions
        ] === FormatOption.Actions_LinkOnly;

      return !original ? (
        <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
      ) : (
        <BaseCell>
          <SalesActionsCell
            linkOnly={linkOnly}
            saleId={original.sale.id}
            marketplaceSaleId={original.sale.idOnMkp}
            marketplace={original.sale.mkp}
            saleStatus={original.sale.status}
            actions={original.sale.actions}
            ticketType={original.sale.ticketType}
            isNoFulfill={original.sale.isNoFulfill}
          />
        </BaseCell>
      );
    },
  },
  [SalesTableColumnId.FulfillmentDate]: createDateColumn<SaleWithEvent | null>({
    id: SalesTableColumnId.FulfillmentDate,
    headerContentId:
      SALE_TABLE_V2_COLUMN_ID_TO_CONTENT_ID[SalesTableColumnId.FulfillmentDate],
    accessor: (data) => data?.sale?.fulfillDate,
    size: COLUMN_DEFAULT_SIZE_L,
    defaultPrecision: FormatOption.DateTime_MonthDD_Time,
  }),
  [SalesTableColumnId.AutoFulfill]: createTextColumn<SaleWithEvent | null>({
    id: SalesTableColumnId.AutoFulfill,
    headerContentId:
      SALE_TABLE_V2_COLUMN_ID_TO_CONTENT_ID[SalesTableColumnId.AutoFulfill],
    accessor: (data) => data?.sale?.isNoFulfill,
    size: COLUMN_DEFAULT_SIZE_LG,
    customCellRenderer: (props: any) => (
      <BaseCell>
        <Content id={props.value ? ContentId.No : ContentId.Yes} />
      </BaseCell>
    ),
  }),
  [SalesTableColumnId.PaymentState]: createStatusColumn({
    id: SalesTableColumnId.PaymentState,
    headerContentId:
      SALE_TABLE_V2_COLUMN_ID_TO_CONTENT_ID[SalesTableColumnId.PaymentState],
    accessor: (data) => {
      return data?.sale?.mkpPmtState ?? data?.sale?.pmtState;
    },
    statusMapping: (data) => {
      if (data?.sale?.mkpPmtState != null) {
        return MARKETPLACE_SALE_PAYMENT_STATUS_MAPPING;
      }
      if (data?.sale?.pmtState != null) {
        return SALE_PAYMENT_STATUS_MAPPING;
      }
      return undefined;
    },
    contentMapping: (data) => {
      if (data?.sale?.mkpPmtState != null) {
        return MARKETPLACE_SALE_PAYMENT_STATE_TO_CID;
      }
      if (data?.sale?.pmtState != null) {
        return SALE_PAYMENT_STATE_TO_CID;
      }
      return undefined;
    },
    size: COLUMN_DEFAULT_SIZE_LG,
  }),

  [SalesTableColumnId.PaymentReceived]: createTextColumn<SaleWithEvent | null>({
    id: SalesTableColumnId.PaymentReceived,
    headerContentId:
      SALE_TABLE_V2_COLUMN_ID_TO_CONTENT_ID[SalesTableColumnId.PaymentReceived],
    accessor: (data) => data?.sale?.pmtRcv,
    size: COLUMN_DEFAULT_SIZE_LG,
    customCellRenderer: (props: any) => (
      <BaseCell>
        <Content id={props.value ? ContentId.Yes : ContentId.No} />
      </BaseCell>
    ),
    enableColumnFilter: true,
    filterFn: booleanFilter,
    meta: {
      columnFilterVariant: 'boolean',
    },
  }),
  [SalesTableColumnId.PaymentReferenceId]:
    createTextColumn<SaleWithEvent | null>({
      id: SalesTableColumnId.PaymentReferenceId,
      headerContentId:
        SALE_TABLE_V2_COLUMN_ID_TO_CONTENT_ID[
          SalesTableColumnId.PaymentReferenceId
        ],
      accessor: (data) => data?.sale?.pmtRefId,
      size: COLUMN_DEFAULT_SIZE_LG,
      enableColumnFilter: true,
      filterFn: textSearchFilter,
      meta: {
        columnFilterVariant: 'textSearch',
      },
    }),
  [SalesTableColumnId.PurchaseCreatedBy]:
    createUserTagColumn<SaleWithEvent | null>({
      id: SalesTableColumnId.PurchaseCreatedBy,
      headerContentId:
        SALE_TABLE_V2_COLUMN_ID_TO_CONTENT_ID[
          SalesTableColumnId.PurchaseCreatedBy
        ],
      accessor: (data) => data?.sale?.owners?.poInsertBys,
      useUserDisplayHook: useContextBasedUsers,
      size: COLUMN_DEFAULT_SIZE_LG,
    }),
  [SalesTableColumnId.PurchasedBy]: createUserTagColumn<SaleWithEvent | null>({
    id: SalesTableColumnId.PurchasedBy,
    headerContentId:
      SALE_TABLE_V2_COLUMN_ID_TO_CONTENT_ID[SalesTableColumnId.PurchasedBy],
    accessor: (data) => data?.sale?.owners?.buyerIds,
    useUserDisplayHook: useContextBasedUsers,
    size: COLUMN_DEFAULT_SIZE_LG,
  }),
  [SalesTableColumnId.PurchaseDate]: createDateColumn<SaleWithEvent | null>({
    id: SalesTableColumnId.PurchaseDate,
    headerContentId:
      SALE_TABLE_V2_COLUMN_ID_TO_CONTENT_ID[SalesTableColumnId.PurchaseDate],
    accessor: (data) => data?.sale?.poDate,
    size: COLUMN_DEFAULT_SIZE_L,
    defaultPrecision: FormatOption.DateTime_MonthDD_Time,
  }),
  [SalesTableColumnId.VendorOrderId]: createLinkColumn<SaleWithEvent | null>({
    id: SalesTableColumnId.VendorOrderId,
    headerContentId:
      SALE_TABLE_V2_COLUMN_ID_TO_CONTENT_ID[SalesTableColumnId.VendorOrderId],
    linkAccessor: (data) =>
      data?.sale?.poId ? getPurchaseOrderRelativeUrl(data?.sale?.poId) : null,
    textAccessor: (data) => String(data?.sale?.poVendOrdId),
    size: COLUMN_DEFAULT_SIZE_BASE_L,
    sectionType: SectionType.RecentSalesTable,
  }),
  [SalesTableColumnId.VendorAccountEmail]:
    createCopyColumn<SaleWithEvent | null>({
      id: SalesTableColumnId.VendorAccountEmail,
      headerContentId:
        SALE_TABLE_V2_COLUMN_ID_TO_CONTENT_ID[
          SalesTableColumnId.VendorAccountEmail
        ],
      accessor: (data) => data?.sale?.poVendAccEmail?.toLowerCase(),
      size: COLUMN_DEFAULT_SIZE_LG,
      enableColumnFilter: true,
      filterFn: multiSelectFilter,
      meta: {
        columnFilterVariant: 'multiSelect',
      },
    }),
  [SalesTableColumnId.OriginatedBy]: createUserTagColumn<SaleWithEvent | null>({
    id: SalesTableColumnId.OriginatedBy,
    headerContentId:
      SALE_TABLE_V2_COLUMN_ID_TO_CONTENT_ID[SalesTableColumnId.OriginatedBy],
    accessor: (data) => data?.sale?.origBy,
    useUserDisplayHook: useApiFetchedUser,
    size: COLUMN_DEFAULT_SIZE_LG,
  }),
  [SalesTableColumnId.Row]: {
    id: SalesTableColumnId.Row,
    enableColumnFilter: true,
    meta: {
      columnFilterVariant: 'multiSelect',
    },
    filterFn: multiSelectFilter,
    size: COLUMN_DEFAULT_SIZE_LG,
    minSize: COLUMN_MIN_SIZE,
    maxSize: COLUMN_MAX_SIZE,
    header: ({ column: { id } }) => (
      <Content
        id={SALE_TABLE_V2_COLUMN_ID_TO_CONTENT_ID[id as SalesTableColumnId]}
      />
    ),
    accessorFn: (data) => {
      return data?.sale?.seating?.row;
    },
    cell: function Cell({ getValue, row: { original } }) {
      const value = getValue<string>();

      if (!original) {
        return (
          <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
        );
      }
      if (!original.sale) return null;

      return <BaseCell>{value}</BaseCell>;
    },
    sortingFn: sortGeneric,
  },
  [SalesTableColumnId.Seats]: {
    id: SalesTableColumnId.Seats,
    enableColumnFilter: true,
    size: COLUMN_DEFAULT_SIZE_LG,
    minSize: COLUMN_MIN_SIZE,
    maxSize: COLUMN_MAX_SIZE,
    header: ({ column: { id } }) => (
      <Content
        id={SALE_TABLE_V2_COLUMN_ID_TO_CONTENT_ID[id as SalesTableColumnId]}
      />
    ),
    accessorFn: (data) => {
      return data?.sale?.seating;
    },
    filterFn: seatRangeFilter  as FilterFnOption<SaleWithEvent | null>,
    meta: {
      columnFilterVariant: 'numberRange',
    },
    cell: function Cell({ row: { original } }) {
      if (!original) {
        return (
          <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
        );
      }
      if (!original.sale) return null;

      const { seatFr, seatTo } = original.sale.seating;
      const seatDisplay = getSeatDisplayText({
        seatFr,
        seatTo,
      });
      return <BaseCell>{seatDisplay}</BaseCell>;
    },
    sortingFn: sortSeatingSeatsForTable,
  },
  [SalesTableColumnId.TotalPaymentsAmount]: createMoneyColumn({
    id: SalesTableColumnId.TotalPaymentsAmount,
    headerContentId:
      SALE_TABLE_V2_COLUMN_ID_TO_CONTENT_ID[
        SalesTableColumnId.TotalPaymentsAmount
      ],
    accessor: (data) => data?.sale?.totalPmt,
    withFooter: true,
  }),
  [SalesTableColumnId.TotalConvertedPaymentsAmount]: createMoneyColumn({
    id: SalesTableColumnId.TotalConvertedPaymentsAmount,
    headerContentId:
      SALE_TABLE_V2_COLUMN_ID_TO_CONTENT_ID[
        SalesTableColumnId.TotalConvertedPaymentsAmount
      ],
    accessor: (data) => data?.sale?.totalConvertedPmt,
    withFooter: true,
  }),
  [SalesTableColumnId.WastedQuantity]: createNumberColumn<SaleWithEvent | null>(
    {
      id: SalesTableColumnId.WastedQuantity,
      headerContentId:
        SALE_TABLE_V2_COLUMN_ID_TO_CONTENT_ID[
          SalesTableColumnId.WastedQuantity
        ],
      accessor: (data) => data?.sale?.qtyWasted,
      minSize: COST_COLUMN_MIN_SIZE,
      maxSize: COST_COLUMN_MAX_SIZE,
      withFooter: true,
      useUserPrecision: false,
      enableColumnFilter: true,
    }
  ),

  [SalesTableColumnId.InternalNotes]: createTextColumn<SaleWithEvent | null>({
    id: SalesTableColumnId.InternalNotes,
    headerContentId:
      SALE_TABLE_V2_COLUMN_ID_TO_CONTENT_ID[SalesTableColumnId.InternalNotes],
    accessor: (data) => data?.sale?.internalNotesPreview,
    enableColumnFilter: true,
    filterFn: textSearchFilter,
    meta: {
      columnFilterVariant: 'textSearch',
    },
  }),

  [SalesTableColumnId.ConsigneeSellerAccountId]: {
    id: SalesTableColumnId.ConsigneeSellerAccountId,
    enableColumnFilter: false,
    size: COLUMN_DEFAULT_SIZE_LG,
    minSize: COLUMN_MIN_SIZE,
    maxSize: COLUMN_MAX_SIZE,
    header: () => (
      <Content
        id={
          SALE_TABLE_V2_COLUMN_ID_TO_CONTENT_ID[
            SalesTableColumnId.ConsigneeSellerAccountId
          ]
        }
      />
    ),
    accessorFn: (data) => data?.sale?.accId,
    cell: function Cell({ getValue, row: { original } }) {
      const { accountName } =
        useConsigneeAccountDisplayName(getValue<string | null>());

      if (original == null) {
        return (
          <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
        );
      }

      return <BaseCell>{accountName}</BaseCell>;
    },
  },
};
